import React, { useState } from "react";
import { X, ShieldCheck, Copy, Check, Key, Lock, ExternalLink, Cpu } from "lucide-react";
// import baseapi from "../../../services/baseapi";
import integrationapi from "../../../services/integrationapi";
import { showError, showSuccess } from "../../../utils/toast";
import { useClientLevel } from "../../../hooks/useClientLevel";

interface ExternalApiModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const ExternalApiModal: React.FC<ExternalApiModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const level = useClientLevel();
    const [isLoading, setIsLoading] = useState(false);
    const [installationData, setInstallationData] = useState<any>(null);
    const [copyStates, setCopyStates] = useState<{ [key: string]: boolean }>({});

    React.useEffect(() => {
        if (isOpen) {
            setInstallationData(null);
            setIsLoading(false);
            setCopyStates({});
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInstall = async () => {
        setIsLoading(true);
        try {
            const response = await integrationapi.get("external/install");

            if (response.data.status) {
                setInstallationData(response.data.data);
                showSuccess(response.data.message || "External API Integration installed successfully");
            } else {
                showError("Installation failed");
            }
        } catch (error: any) {
            console.error("Connect integrationapi external/install failed, trying fallback...", error);
            try {
                const payload: any = {};
                if (level && level.name !== "global" && level.id) {
                    payload.client_id = level.id;
                }
                const response = await integrationapi.post("integrations/external-api/install", payload);

                if (response.data.status) {
                    setInstallationData(response.data.data);
                    showSuccess(response.data.message || "External API Integration installed successfully");
                    return;
                }
            } catch (fallbackError: any) {
                console.error("Fallback install failed:", fallbackError);
            }
            showError(error.response?.data?.message || error.message || "An error occurred during installation");
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = (text: string, field: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopyStates(prev => ({ ...prev, [field]: true }));
        setTimeout(() => {
            setCopyStates(prev => ({ ...prev, [field]: false }));
        }, 2000);
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in duration-300 p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative bg-[#0F172A] rounded-[2rem] border border-white/5 shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in duration-300 flex flex-col">
                {/* Premium Header */}
                <div className="bg-gradient-to-b from-[#1E293B] to-[#0F172A] px-6 py-5 flex items-center justify-between shrink-0 border-b border-white/5 shadow-xl">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center overflow-hidden shrink-0">
                            <Cpu className="text-orange-500 w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white tracking-tight uppercase">External API</h2>
                            <p className="text-orange-500/60 text-[9px] font-black uppercase tracking-[0.2em] leading-none mt-1.5">
                                {installationData ? 'Operational' : 'Initialization'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (installationData) onSuccess();
                            onClose();
                        }}
                        className="text-zinc-500 hover:text-white transition-all p-2 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8">
                    {!installationData ? (
                        <div className="space-y-6">
                            <div className="bg-white/5 border border-white/5 p-5 rounded-2xl flex flex-col gap-3 relative overflow-hidden group shadow-inner">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 blur-3xl -mr-12 -mt-12 transition-all group-hover:bg-orange-500/10" />
                                <p className="text-xs text-zinc-400 font-medium leading-relaxed relative z-10">
                                    Unlock advanced capabilities by generating secure API credentials. Seamlessly connect external services and build custom workflows with high-level security protocols.
                                </p>
                            </div>

                            <button
                                onClick={handleInstall}
                                disabled={isLoading}
                                className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-2xl shadow-[0_10px_30px_rgba(234,88,12,0.3)] hover:shadow-[0_10px_40px_rgba(234,88,12,0.4)] transition-all active:scale-[0.98] disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center gap-3 group relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:animate-shimmer" />
                                {isLoading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <span className="uppercase tracking-widest text-[11px] font-black">Enable Integration</span>
                                        <ShieldCheck size={16} className="transition-transform group-hover:scale-110" />
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            {/* Success State */}
                            <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                                    <Check className="text-white" size={18} strokeWidth={3} />
                                </div>
                                <div>
                                    <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest">Installation Successful</p>
                                    <p className="text-zinc-500 text-[9px] font-bold mt-0.5">Secure credentials generated.</p>
                                </div>
                            </div>

                            {/* Credentials Display */}
                            <div className="space-y-4">
                                {/* API Key */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Key size={12} className="text-orange-500" /> API KEY
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={installationData.api_key}
                                            className="w-full pl-4 pr-12 py-4 bg-black/40 text-orange-400 rounded-2xl font-mono text-[10px] border border-white/5 focus:border-orange-500/30 focus:ring-0 shadow-inner truncate transition-all"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(installationData.api_key, 'key')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-all border border-white/5 shadow-lg"
                                        >
                                            {copyStates['key'] ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-zinc-500" />}
                                        </button>
                                    </div>
                                </div>

                                {/* API Secret */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Lock size={12} className="text-orange-500" /> API SECRET
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={installationData.api_secret}
                                            className="w-full pl-4 pr-12 py-4 bg-black/40 text-orange-400 rounded-2xl font-mono text-[10px] border border-white/5 focus:border-orange-500/30 focus:ring-0 shadow-inner truncate transition-all"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(installationData.api_secret, 'secret')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-all border border-white/5 shadow-lg"
                                        >
                                            {copyStates['secret'] ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-zinc-500" />}
                                        </button>
                                    </div>
                                </div>

                                {/* API Endpoint */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <ExternalLink size={12} className="text-orange-500" /> ENDPOINT
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={installationData.api_url}
                                            className="w-full pl-4 pr-12 py-4 bg-black/40 text-orange-400 rounded-2xl font-mono text-[10px] border border-white/5 focus:border-orange-500/30 focus:ring-0 shadow-inner truncate transition-all"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(installationData.api_url, 'url')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-all border border-white/5 shadow-lg"
                                        >
                                            {copyStates['url'] ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-zinc-500" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    onSuccess();
                                    onClose();
                                }}
                                className="w-full py-4 bg-white/5 hover:bg-white/10 text-white text-[11px] font-black uppercase tracking-[0.2em] rounded-2xl transition-all border border-white/5 active:scale-[0.98]"
                            >
                                Finalize Setup
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExternalApiModal;
